import cn from "classnames"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CSSTransition } from "react-transition-group"
import Icons from "@/images/signomatic/fixmethods"
import { CheckmarkSmall, ChevronDown, CloseLarge } from "@/skylticons"
import "../styles/modal.css"

interface ModalBodyProps {
  skipPadding?: boolean
  children?: React.ReactNode
}

const ModalBody: React.FC<ModalBodyProps> = ({ skipPadding, children }) => (
  <div className={cn("modal-body", { "no-padding": skipPadding })}>{children}</div>
)

interface ModalActionsProps {
  right?: boolean
  children?: React.ReactNode
}

const ModalActions: React.FC<ModalActionsProps> = ({ right, children }) => (
  <div className={cn("modal-actions", { right })}>{children}</div>
)

interface ModalProps {
  open?: boolean
  side?: boolean
  onClose?: () => void
  onOverlayClick?: () => void
  className?: string
  closeText?: string
  children?: React.ReactNode
}

const Modal: React.FC<ModalProps> = ({ children, open, side, onClose, onOverlayClick, className, closeText }) => {
  const overlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target instanceof Element && e.target.closest(".content")) {
        return
      }

      if (onOverlayClick) {
        onOverlayClick()
      }
    },
    [onOverlayClick]
  )

  return (
    <CSSTransition classNames="animation" in={open} exit timeout={400}>
      <div className={cn("modal", className, { "has-close": !!onClose, side })}>
        <div className="overlay" onClick={overlayClick}>
          <div className="content z-0 max-h-full overflow-y-auto">
            {onClose && (
              <a className="close button button-large button-subtle icon-only z-10 mb-0" onClick={onClose}>
                <CloseLarge />
                {closeText && <span className="ml-2 hidden md:block">{closeText}</span>}
              </a>
            )}

            {children}
          </div>
        </div>
      </div>
    </CSSTransition>
  )
}

interface SignData {
  [k: string]: boolean | string | null | string[] | undefined

  key: string
  name: string
  signomatic_path: string
  product_path: string
  image: string
  image2x: string | null
  image_hover: string | null
  image_hover2x: string | null
  description: string
  usp?: string
  usage_outdoor: boolean
  usage_house: boolean
  usage_door: boolean
  usage_clothing: boolean
  usage_events: boolean
  usage_materials: string[]
  fix_methods: string[]
  admin_only?: boolean
}

interface ProductProps {
  data: SignData
}

const Product: React.FC<ProductProps> = ({ data }) => {
  const { t } = useTranslation()

  const srcSet = [`${data.image} 1x`]
  const hoverSrcSet = [`${data.image_hover} 1x`]

  if (data.image2x) {
    srcSet.push(`${data.image2x} 2x`)
  }

  if (data.image_hover2x) {
    hoverSrcSet.push(`${data.image_hover2x} 2x`)
  }

  return (
    <div className={cn("product", data.key)}>
      <a href={data.product_path} className="overlay" data-gtm="product-design-button"></a>
      <div className="image">
        <picture>
          <img src={data.image} srcSet={srcSet.join(", ")} loading="lazy" />
        </picture>

        {data.image_hover && (
          <picture>
            <img className="hover" src={data.image_hover} srcSet={hoverSrcSet.join(", ")} loading="lazy" />
          </picture>
        )}
      </div>
      <h3 className="heading-five">{data.name}</h3>
      <p className="paragraph-xs">{data.description}</p>
      <div className="usp-wrapper">
        {data.usp && (
          <div className="usp">
            <CheckmarkSmall />
            <p className="paragraph-xs bold mb-0">{data.usp}</p>
          </div>
        )}
      </div>
      <div className="buttons z-10">
        {data.admin_only !== true && (
          <a
            href={data.signomatic_path}
            className="button button-medium button-emphasis mb-0"
            data-gtm="product-design-button"
          >
            {t("sign.design")}
          </a>
        )}
        <a
          href={data.product_path}
          className="button button-medium button-secondary mb-0"
          data-gtm="product-read-more-button"
        >
          {t("misc.readmore")}
        </a>
      </div>
    </div>
  )
}

const arraysIntersect = (a: any[], b: any[]) => {
  return !!a.find(m => b.includes(m))
}

const fixmethodIcon = (map: Record<string, string[] | undefined>, name: string) => {
  const firstValue = (map[name] || [])[0]
  return Icons[firstValue] && React.createElement(Icons[firstValue])
}

enum Section {
  material,
  area,
  fixmethod,
}

const areas = ["usage_outdoor", "usage_house", "usage_door", "usage_clothing", "usage_events"]
const initialFixmethods = [
  "tape",
  "hanging",
  "magnet_rug",
  "pole",
  "screw",
  "tape_and_screw",
  "screw_capped",
  "wall_distance",
]

export interface ProductFilterProps extends Record<string, unknown> {
  data: SignData[]
  locale: string
}

const ProductFilter: React.FC<ProductFilterProps> = ({ data, locale }) => {
  const { t } = useTranslation()
  const [ready, setReady] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [materialsOpen, setMaterialsOpen] = useState(false)
  const [areaOpen, setAreaOpen] = useState(false)
  const [fixmethodsOpen, setFixmethodsOpen] = useState(false)
  const [fixmethodsExpanded, setFixmethodsExpanded] = useState(false)
  const materialRef = useRef<HTMLDivElement>(null)
  const areaRef = useRef<HTMLDivElement>(null)
  const fixmethodsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setReady(true)
  }, [])

  const toggleFixmethodsExpanded = useCallback(() => {
    setFixmethodsExpanded(v => !v)
  }, [])

  const scrollToSection = useCallback((section: Section) => {
    switch (section) {
      case Section.material:
        requestAnimationFrame(() => materialRef.current?.scrollIntoView({ behavior: "smooth" }))
        break
      case Section.area:
        requestAnimationFrame(() => areaRef.current?.scrollIntoView({ behavior: "smooth" }))
        break
      case Section.fixmethod:
        requestAnimationFrame(() => fixmethodsRef.current?.scrollIntoView({ behavior: "smooth" }))
        break
    }
  }, [])

  const openModal = useCallback(
    (section: Section) => {
      switch (section) {
        case Section.material:
          setMaterialsOpen(true)
          break
        case Section.area:
          setAreaOpen(true)
          break
        case Section.fixmethod:
          setFixmethodsOpen(true)
          break
      }

      setModalOpen(true)
      scrollToSection(section)
    },
    [scrollToSection]
  )

  const toggleSection = useCallback(
    (section: Section) => {
      switch (section) {
        case Section.material:
          setMaterialsOpen(v => {
            !v && scrollToSection(section)
            return !v
          })
          break
        case Section.area:
          setAreaOpen(v => {
            !v && scrollToSection(section)
            return !v
          })
          break
        case Section.fixmethod:
          setFixmethodsOpen(v => {
            !v && scrollToSection(section)
            return !v
          })
          break
      }
    },
    [scrollToSection]
  )

  const sectionKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, section: Section) => {
      if (!["Enter", " "].includes(e.key)) {
        return
      }

      e.preventDefault()

      toggleSection(section)
    },
    [toggleSection]
  )

  const toggleModal = useCallback(() => {
    setModalOpen(v => !v)
  }, [])

  const materials = useMemo(
    () =>
      Array.from(new Set(data.map(s => s.usage_materials).flat())).sort((a, b) =>
        t(`material_type.${a}`) > t(`material_type.${b}`) ? 1 : -1
      ),
    [data, t]
  )
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([])

  const [selectedAreas, setSelectedAreas] = useState<string[]>([])

  const fixmethodMap = useMemo(() => {
    const result: Record<string, string[] | undefined> = {}
    for (const method of Array.from(new Set(data.map(s => s.fix_methods).flat()))) {
      const name = t(`fixmethod.${method}`)

      const list = (result[name] ||= [])
      list.push(method)
    }

    return result
  }, [data, t])

  const fixmethods = Object.keys(fixmethodMap).sort()
  const [selectedFixmethods, setSelectedFixmethods] = useState<string[]>([])

  const toggleOption = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
      const name = e.target.name

      if (e.target.checked) {
        setter(current => [...current, name])
      } else {
        setter(current => current.filter(value => value !== name))
      }
    },
    []
  )

  const toggleFixmethods = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name
      const values = fixmethodMap[name]

      if (!values) {
        return
      }

      if (e.target.checked) {
        setSelectedFixmethods(current => [...current, ...values])
      } else {
        setSelectedFixmethods(current => current.filter(value => !values.includes(value)))
      }
    },
    [fixmethodMap]
  )

  const removeTag = useCallback((name: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(current => current.filter(value => value !== name))
  }, [])

  const removeFixmethod = useCallback(
    (name: string) => {
      const values = fixmethodMap[name]

      if (!values) {
        return
      }

      setSelectedFixmethods(current => current.filter(value => !values.includes(value)))
    },
    [fixmethodMap]
  )

  const clearFilters = useCallback(() => {
    setSelectedMaterials([])
    setSelectedAreas([])
    setSelectedFixmethods([])
  }, [])

  const selectedFixmethodNames = Array.from(new Set(selectedFixmethods.map(m => t(`fixmethod.${m}`))))

  const materialActive = selectedMaterials.length > 0
  const areaActive = selectedAreas.length > 0
  const fixmethodsActive = selectedFixmethodNames.length > 0
  const filtersActive = selectedMaterials.length + selectedAreas.length + selectedFixmethodNames.length
  const anyFilters = materialActive || areaActive || fixmethodsActive

  const activeProducts = data.filter(p => {
    if (materialActive && !arraysIntersect(selectedMaterials, p.usage_materials)) {
      return false
    }

    if (areaActive && !selectedAreas.find(attr => p[attr])) {
      return false
    }

    if (fixmethodsActive && !arraysIntersect(selectedFixmethods, p.fix_methods)) {
      return false
    }

    if (p.key === "tactile_sign" && locale !== "sv-SE") {
      return false
    }

    return true
  })

  return (
    <>
      <div className="container">
        <div className="title">
          <h2 className="mb-0">{t("productfilter.title")}</h2>
        </div>
      </div>

      <div className="filterbar-container">
        <div className="container">
          <div className="filterbar">
            <div className="buttons">
              <button
                onClick={() => openModal(Section.material)}
                className={cn("button button-medium flat mb-0", materialActive ? "button-primary" : "button-secondary")}
                disabled={!ready}
                data-gtm="filter-button-material"
                type="button"
              >
                {t("productfilter.material")}
                {materialActive && ` (${selectedMaterials.length})`}
              </button>
              <button
                onClick={() => openModal(Section.area)}
                className={cn("button button-medium flat mb-0", areaActive ? "button-primary" : "button-secondary")}
                disabled={!ready}
                data-gtm="filter-button-use-area"
                type="button"
              >
                {t("productfilter.use_areas")}
                {areaActive && ` (${selectedAreas.length})`}
              </button>
              <button
                onClick={() => openModal(Section.fixmethod)}
                className={cn(
                  "button button-medium flat mb-0",
                  fixmethodsActive ? "button-primary" : "button-secondary"
                )}
                disabled={!ready}
                data-gtm="filter-button-fixing-method"
                type="button"
              >
                {t("productfilter.fixmethods")}
                {fixmethodsActive && ` (${selectedFixmethodNames.length})`}
              </button>
              {anyFilters && (
                <button onClick={clearFilters} className="button button-subtle button-error mb-0" type="button">
                  {t("productfilter.clear_all")}
                </button>
              )}
            </div>
          </div>
        </div>

        <hr />
      </div>

      <div className="products-container container">
        {anyFilters && (
          <div className="tags">
            {selectedMaterials.map(material => (
              <div
                key={material}
                className="tag"
                tabIndex={0}
                onClick={() => removeTag(material, setSelectedMaterials)}
                onKeyDown={() => removeTag(material, setSelectedMaterials)}
              >
                <div className="paragraph-2xs mb-0">{t(`material_type.${material}`)}</div>
                <CloseLarge />
              </div>
            ))}
            {selectedAreas.map(area => (
              <div
                key={area}
                className="tag"
                tabIndex={0}
                onClick={() => removeTag(area, setSelectedAreas)}
                onKeyDown={() => removeTag(area, setSelectedAreas)}
              >
                <div className="paragraph-2xs mb-0">{t(`productfilter.${area}`)}</div>
                <CloseLarge />
              </div>
            ))}
            {selectedFixmethodNames.map(method => (
              <div
                key={method}
                className="tag"
                tabIndex={0}
                onClick={() => removeFixmethod(method)}
                onKeyDown={() => removeFixmethod(method)}
              >
                <div className="paragraph-2xs mb-0">{method}</div>
                <CloseLarge />
              </div>
            ))}
          </div>
        )}

        <div className="product-list grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5">
          {activeProducts.map(d => (
            <Product key={d.key} data={d} />
          ))}
        </div>
      </div>

      <Modal
        className="productfilter-modal z-40"
        open={modalOpen}
        onClose={toggleModal}
        onOverlayClick={toggleModal}
        side
      >
        <ModalBody>
          <div className={cn("filter-accordion", { open: materialsOpen })}>
            <div
              className="filter-accordion-header"
              tabIndex={0}
              ref={materialRef}
              onClick={() => toggleSection(Section.material)}
              onKeyDown={e => sectionKeydown(e, Section.material)}
            >
              <div className="content">
                <div className="paragraph bold mb-0">{t("productfilter.material")}</div>
                {!materialsOpen && materialActive && (
                  <div className="value paragraph-sm mb-0">
                    {selectedMaterials.map(material => t(`material_type.${material}`)).join(", ")}
                  </div>
                )}
              </div>
              <ChevronDown />
            </div>

            <ul className="filter-accordion-items">
              {materials.map(material => (
                <div key={material} className="checkbox large">
                  <input
                    name={material}
                    id={`material_${material}`}
                    type="checkbox"
                    className="form-control"
                    checked={selectedMaterials.includes(material)}
                    onChange={e => toggleOption(e, setSelectedMaterials)}
                  />
                  <label htmlFor={`material_${material}`} className="checkbox-label paragraph-xs">
                    {t(`material_type.${material}`)}
                  </label>
                </div>
              ))}
            </ul>
          </div>

          <div className={cn("filter-accordion", { open: areaOpen })}>
            <div
              className="filter-accordion-header"
              tabIndex={0}
              ref={areaRef}
              onClick={() => toggleSection(Section.area)}
              onKeyDown={e => sectionKeydown(e, Section.area)}
            >
              <div className="content">
                <div className="paragraph bold mb-0">{t("productfilter.use_areas")}</div>
                {!areaOpen && areaActive && (
                  <div className="value paragraph-sm mb-0">
                    {selectedAreas.map(area => t(`productfilter.${area}`)).join(", ")}
                  </div>
                )}
              </div>
              <ChevronDown />
            </div>

            <ul className="filter-accordion-items">
              {areas.map(area => (
                <div key={area} className="checkbox large">
                  <input
                    name={area}
                    id={`area_${area}`}
                    type="checkbox"
                    className="form-control"
                    checked={selectedAreas.includes(area)}
                    onChange={e => toggleOption(e, setSelectedAreas)}
                  />
                  <label htmlFor={`area_${area}`} className="checkbox-label paragraph-xs">
                    {t(`productfilter.${area}`)}
                  </label>
                </div>
              ))}
            </ul>
          </div>

          <div className={cn("filter-accordion", { open: fixmethodsOpen })}>
            <div
              className="filter-accordion-header"
              tabIndex={0}
              ref={fixmethodsRef}
              onClick={() => toggleSection(Section.fixmethod)}
              onKeyDown={e => sectionKeydown(e, Section.fixmethod)}
            >
              <div className="content">
                <div className="paragraph bold mb-0">{t("productfilter.fixmethods")}</div>
                {!fixmethodsOpen && fixmethodsActive && (
                  <div className="value paragraph-sm mb-0">{selectedFixmethodNames.join(", ")}</div>
                )}
              </div>
              <ChevronDown />
            </div>

            {fixmethodsExpanded && (
              <a className="paragraph-sm bold mb-0" onClick={toggleFixmethodsExpanded}>
                - {t("productfilter.fewer_fixmethods")}
              </a>
            )}

            <ul className="filter-accordion-items">
              {fixmethods.map(fixmethod => {
                if (!fixmethodsExpanded && !arraysIntersect(initialFixmethods, fixmethodMap[fixmethod] || [])) {
                  return null
                }

                return (
                  <div key={fixmethod} className="checkbox large">
                    <input
                      name={fixmethod}
                      id={`fixmethod_${fixmethod}`}
                      type="checkbox"
                      className="form-control"
                      checked={arraysIntersect(selectedFixmethods, fixmethodMap[fixmethod] || [])}
                      onChange={e => toggleFixmethods(e)}
                    />
                    <label htmlFor={`fixmethod_${fixmethod}`} className="checkbox-label paragraph-xs">
                      {fixmethodIcon(fixmethodMap, fixmethod)}
                      {fixmethod}
                    </label>
                  </div>
                )
              })}
            </ul>

            {!fixmethodsExpanded && (
              <a className="paragraph-sm bold" onClick={toggleFixmethodsExpanded}>
                + {t("productfilter.more_fixmethods")}
              </a>
            )}
          </div>
        </ModalBody>
        <ModalActions>
          <button className="button button-secondary" onClick={clearFilters} disabled={!anyFilters} type="button">
            {t("productfilter.clear_all")}
          </button>
          <button className="button button-primary" onClick={toggleModal} type="button">
            {t("productfilter.apply_filter")}
            {filtersActive > 0 && ` (${filtersActive})`}
          </button>
        </ModalActions>
      </Modal>
    </>
  )
}

export default ProductFilter
